"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_SYMBOL_LAYOUT_STRATEGY = void 0;
exports.isUserOwnedPartVersionData = isUserOwnedPartVersionData;
exports.isOrganizationOwnedPartVersionData = isOrganizationOwnedPartVersionData;
exports.isUserOwnedPartData = isUserOwnedPartData;
exports.isOrganizationOwnedPartData = isOrganizationOwnedPartData;
exports.isSystemPart = isSystemPart;
exports.partHasSimModel = partHasSimModel;
exports.partHasBehaviour = partHasBehaviour;
exports.getPartType = getPartType;
exports.matchesPartType = matchesPartType;
const constants_1 = require("@buildwithflux/constants");
const identifier_1 = require("../identifier");
const constants_2 = require("./constants");
exports.DEFAULT_SYMBOL_LAYOUT_STRATEGY = "parametric-v0";
function isUserOwnedPartVersionData(partVersionData) {
    return partVersionData.organization_owner_uid === undefined;
}
function isOrganizationOwnedPartVersionData(partVersionData) {
    return (0, identifier_1.isOrganizationUid)(partVersionData.organization_owner_uid);
}
function isUserOwnedPartData(partData) {
    return partData.organization_owner_uid === undefined;
}
function isOrganizationOwnedPartData(partData) {
    return (0, identifier_1.isOrganizationUid)(partData.organization_owner_uid);
}
/**
 * @see PartStorageHelper that has similar functions.
 */
function isSystemPart(partUid) {
    return [
        constants_2.ROUTE_BRANCH_POINT_TERMINAL_UID,
        constants_2.ROUTE_BRANCH_POINT_PART_UID,
        constants_2.GENESIS_TERMINAL_PART_UID,
        constants_2.DEFAULT_GROUND_TERMINAL_PART_UID,
        // QUESTION: why is the ground portal not included in isSystemPart check?
    ].includes(partUid);
}
/**
 * Whether a part has a complete set of behaviours that can be used by the
 * usercode_runtime. For example, in the following nested hierarchy, the topmost
 * parents has a sim model by derivation.
 *
 *   -parent: no behaviour
 *   --child: behaviour
 *   --child: behaviour
 *   --parent: no behaviour
 *   ---child: behaviour
 *   ---child: behaviour
 *
 */
function partHasSimModel(partVersionData) {
    // TODO: Reconcile this with the copy at frontend/src/modules/usercode_runtime/predicates.ts
    // It was cloned there so it can handle inferred models, which can't be done in `core` currently.
    return ((partHasBehaviour(partVersionData) && behaviourSetsSimModel(partVersionData)) ||
        partVersionData.behaviour_is_derived ||
        // NOTE: system parts are special cased in the Simulator. See instantiateElement.
        isSystemPart(partVersionData.part_uid));
}
/**
 * Whether a part has user code, minus comments, and without errors.
 */
function partHasBehaviour(partVersionData) {
    return !!removeJsComments(partVersionData.behaviour || "").trim() && !partVersionData.behaviour_has_errors;
}
function removeJsComments(jsCode) {
    return jsCode.replace(/\/\*[\s\S]*?\*\/|\/\/.*/g, "");
}
function behaviourSetsSimModel(partVersionData) {
    return !!partVersionData.behaviour && removeWhitespace(partVersionData.behaviour).includes("flux.simulationModel=");
}
function removeWhitespace(str) {
    return str.replace(/\s/g, "");
}
/**
 * Get what type of part this is e.g. resistor, inductor, etc.
 * @returns Either a value or nothing if the property is not set.
 * @remarks There are multiple matching criteria for the propertis that we look for and two of them are deprecated.  The "name" property
 * is the currently accepted way to find the part type.
 * @privateRemarks TODO: Why not write a migration and get rid of all of the old extraneous data?
 *
 * We only return the part type if it is a string property
 */
function getPartType(partVersion) {
    const properties = Object.values(partVersion.properties);
    const partTypeProperty = properties.find((property) => property.key === constants_1.defaultPropertyDefinitions.part_type.key ||
        property.name === constants_1.defaultPropertyDefinitions.part_type.label ||
        property.name === "Category");
    const value = partTypeProperty?.value;
    if (typeof value !== "string") {
        return undefined;
    }
    return value;
}
/**
 * Normalizes a part type string for comparison: trims whitespace and lowercases.
 */
function normalizePartType(s) {
    return s.trim().toLowerCase();
}
/**
 * Finds the canonical key in PART_TYPE_DEFINITIONS that matches the given part type
 * (case-insensitive, whitespace-trimmed). Returns undefined if not found.
 */
function findPartTypeKey(partType) {
    const normalized = normalizePartType(partType);
    if (!normalized)
        return undefined;
    for (const key of Object.keys(constants_1.PART_TYPE_DEFINITIONS)) {
        if (normalizePartType(key) === normalized)
            return key;
    }
    return undefined;
}
/**
 * Checks if a part type matches a target type, optionally including parent types in the hierarchy.
 * Comparison is case-insensitive and strips leading/trailing whitespace.
 *
 * @param partType - The part type to check (e.g. from getPartType or getElementPartType)
 * @param targetType - The type to match against (e.g. "Resistor", "Capacitor")
 * @param options - Optional configuration
 * @param options.includeParents - If true (default), traverses parentType chain so e.g.
 *   "Variable Resistor" matches "Resistor". If false, only exact match.
 * @returns True if partType matches targetType (exactly or via parent chain when includeParents is true)
 */
function matchesPartType(partType, targetType, options) {
    const includeParents = options?.includeParents ?? true;
    if (!partType)
        return false;
    const normalizedPart = normalizePartType(partType);
    const normalizedTarget = normalizePartType(targetType);
    if (!normalizedPart || !normalizedTarget)
        return false;
    let currentKey = findPartTypeKey(partType) ?? partType.trim();
    while (currentKey) {
        if (normalizePartType(currentKey) === normalizedTarget)
            return true;
        if (!includeParents)
            return false;
        const def = constants_1.PART_TYPE_DEFINITIONS[currentKey];
        currentKey = def?.parentType;
    }
    return false;
}
//# sourceMappingURL=functions.js.map